import { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid2';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Rating from '@mui/material/Rating';
import Avatar from '@mui/material/Avatar';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import { useGuide } from '@/features/guides/hooks/useGuide';
import { GuideDetailBookingCard } from '@/features/guides/components/GuideDetailBookingCard';
import { GuidePricingTable } from '@/features/guides/components/GuidePricingTable';
import { GuidePublicReviewsDialog } from '@/features/guides/components/GuidePublicReviewsDialog';
import { GuideReviewListItem } from '@/features/guide/components/GuideReviewListItem';
import { Button } from '@/shared/components/ui';
import { guidesApi, guidesKeys } from '@/features/guides/api/guides.api';
import { useQuery } from '@tanstack/react-query';
import { parseGuidesSearchParams, buildGuidesSearchParams } from '@/features/guides/utils/searchParams';

const FALLBACK_BANNER = '/images/background.avif';
const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1200&q=80';

const ratingSx = {
  '& .MuiRating-iconFilled': { color: 'secondary.main' },
  '& .MuiRating-iconEmpty': { color: 'divider' },
};

function profileInitials(name?: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export function GuideDetailPage() {
  const { guideId } = useParams<{ guideId: string }>();
  const [searchParams] = useSearchParams();
  const filters = parseGuidesSearchParams(searchParams.toString());
  const { data: guide, isLoading, isError } = useGuide(guideId);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Build back URL with current filters preserved
  const backToGuidesUrl = useMemo(() => {
    const params = buildGuidesSearchParams(filters);
    const queryString = params.toString();
    return queryString ? `/guides?${queryString}` : '/guides';
  }, [filters]);

  const { data: reviewsData } = useQuery({
    queryKey: guidesKeys.reviews(guideId ?? '', { page: 1, limit: 5 }),
    queryFn: () => guidesApi.getReviews(guideId!, { page: 1, limit: 5 }),
    enabled: Boolean(guideId),
  });

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rounded" height={360} sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Skeleton height={200} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Skeleton height={280} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (isError || !guide) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="error">Guide not found or unavailable.</Alert>
        <Button
          component={RouterLink}
          to={backToGuidesUrl}
          intent="ghost"
          sx={{ mt: 2 }}
          startIcon={<ArrowBackIcon />}
        >
          Back to guides
        </Button>
      </Container>
    );
  }

  const location = [guide.city, guide.state].filter(Boolean).join(', ');

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: 6 }}>
      <Container maxWidth="lg" sx={{ pt: 3 }}>
        <Button
          component={RouterLink}
          to={backToGuidesUrl}
          startIcon={<ArrowBackIcon />}
          intent="ghost"
          sx={{ mb: 2 }}
        >
          Back to guides
        </Button>

        {/* Banner with overlapping avatar */}
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'relative',
              borderRadius: (t) => t.radii.lg,
              overflow: 'hidden',
              height: { xs: 200, md: 280 },
              bgcolor: 'backgrounds.sectionAlt',
            }}
          >
            <Box
              component="img"
              src={guide.bannerPhoto || FALLBACK_BANNER}
              alt="Banner"
              sx={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
            
            {/* Logo and info overlay on the left */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(90deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)',
                display: 'flex',
                alignItems: 'center',
                pl: { xs: 3, md: 5 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  component="img"
                  src="/images/trplin-logo.png"
                  alt="Travel Guider"
                  sx={{
                    height: { xs: 50, md: 70 },
                    width: 'auto',
                    filter: 'brightness(0) invert(1)',
                  }}
                />
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      mb: 0.5,
                      textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    }}
                  >
                    Travel Guider
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      textShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    }}
                  >
                    Your journey, our expertise
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Overlapping circular avatar — kept in banner for cover overlap */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translate(-50%, 50%)',
              zIndex: 2,
            }}
          >
            <Avatar
              src={guide.profilePhoto}
              sx={{
                width: { xs: 104, sm: 120 },
                height: { xs: 104, sm: 120 },
                bgcolor: 'primary.main',
                fontSize: { xs: 36, sm: 40 },
                fontWeight: 700,
                border: '4px solid',
                borderColor: 'background.paper',
                boxShadow: 'var(--shadow-lg)',
              }}
            >
              {profileInitials(guide.fullName)}
            </Avatar>
          </Box>
        </Box>

        {/* Name + meta: sits under avatar with clear gap; name stays optically centered */}
        <Box
          sx={{
            textAlign: 'center',
            mb: 4,
            // Half avatar (~60px) clears the banner; +12px breathing room under the icon
            pt: { xs: '64px', sm: '72px' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1.5,
            }}
          >
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{ textAlign: 'center', lineHeight: 1.2 }}
            >
              {guide.fullName}
            </Typography>
            <CheckCircleOutlinedIcon
              sx={{
                color: 'primary.light',
                fontSize: 22,
                position: 'absolute',
                left: '100%',
                ml: 0.75,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            />
          </Box>

          {location && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 2 }}>
              <PlaceOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography color="text.secondary">{location}</Typography>
            </Box>
          )}

          {(guide.reviewCount ?? 0) > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <Rating value={guide.rating ?? 0} readOnly precision={0.1} sx={ratingSx} />
              <Typography fontWeight={600}>{(guide.rating ?? 0).toFixed(1)}</Typography>
              <Typography color="text.secondary">({guide.reviewCount} reviews)</Typography>
            </Box>
          )}
        </Box>

        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 8 }}>
            {/* About Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <InfoOutlinedIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                <Typography variant="h6" fontWeight={700}>
                  About
                </Typography>
              </Box>
              <Typography color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                {guide.bio || 'This guide has not added a bio yet.'}
              </Typography>
            </Paper>

            {(guide.destinationsServed ?? []).length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bgcolor: 'rgba(59, 130, 246, 0.03)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <LocationOnOutlinedIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                  <Typography variant="h6" fontWeight={700}>
                    Destinations served
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {guide.destinationsServed!.map((dest) => (
                    <Chip
                      key={dest}
                      label={dest}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontWeight: 600,
                        '&:hover': { bgcolor: 'primary.dark' },
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            )}

            {(guide.specializations ?? []).length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  mb: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bgcolor: 'rgba(245, 158, 11, 0.03)',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <EmojiEventsOutlinedIcon sx={{ color: 'secondary.main', fontSize: 24 }} />
                  <Typography variant="h6" fontWeight={700}>
                    Specializations
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {guide.specializations!.map((spec) => (
                    <Chip
                      key={spec}
                      label={spec}
                      variant="outlined"
                      sx={{
                        borderColor: 'secondary.main',
                        color: 'secondary.main',
                        fontWeight: 600,
                        borderWidth: 2,
                        '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.08)' },
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            )}

            {/* Pricing Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 4,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                💰 Pricing
              </Typography>
              <GuidePricingTable pricing={guide.pricing} />
            </Paper>

            {/* Photo Gallery Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 4,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                bgcolor: 'rgba(139, 92, 246, 0.03)',
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  📸 Traveler Moments
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Photos shared by travelers who explored with {guide.fullName?.split(' ')[0] || 'this guide'}
                  </Typography>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
                  gap: 2,
                  mb: 2,
                }}
              >
                {[
                  '/images/cappadocia-travel.jpg',
                  '/images/bali.webp',
                  '/images/rome.webp',
                  '/images/temple-tours.webp',
                  '/images/photography-tours.webp',
                  '/images/profile-background.jpg',
                ].map((img, idx) => (
                  <Box
                    key={idx}
                    onClick={() => setSelectedImage(img)}
                    sx={{
                      position: 'relative',
                      paddingBottom: '100%',
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: 'backgrounds.sectionAlt',
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        '& .overlay': {
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={img}
                      alt={`Traveler moment ${idx + 1}`}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    <Box
                      className="overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0,0,0,0.3)',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography sx={{ color: 'white', fontWeight: 600 }}>
                        View
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>

            {/* Reviews Section */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography variant="h6" fontWeight={700}>
                  Reviews
                </Typography>
                {(reviewsData?.summary.reviewCount ?? 0) > 0 && (
                  <Button intent="ghost" onClick={() => setReviewsOpen(true)}>
                    View all
                  </Button>
                )}
              </Box>
              {(reviewsData?.data.length ?? 0) === 0 ? (
                <Typography color="text.secondary">No reviews yet.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {reviewsData!.data.map((review) => (
                    <GuideReviewListItem key={review.id} review={review} />
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <GuideDetailBookingCard
              guide={guide}
              travelDate={filters.travelDate}
              endDate={filters.endDate}
              city={filters.city}
            />
          </Grid>
        </Grid>
      </Container>

      <GuidePublicReviewsDialog
        profileId={guide._id}
        guideName={guide.fullName}
        open={reviewsOpen}
        onClose={() => setReviewsOpen(false)}
      />

      {/* Image Viewer Dialog */}
      <Dialog
        open={Boolean(selectedImage)}
        onClose={() => setSelectedImage(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            maxWidth: '90vw',
            maxHeight: '90vh',
          },
        }}
      >
        <IconButton
          onClick={() => setSelectedImage(null)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            color: 'white',
            zIndex: 1,
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.8)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt="Traveler moment"
              sx={{
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: 2,
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
